import type { SourceRect } from './crop';

/** The stored photo's size. Four times the 38px circle it is drawn in, which
 * covers a retina screen and the larger frame the profile card uses, and is
 * still a small file. */
export const AVATAR_SIZE = 512;

/**
 * Cuts the chosen square out of a picture and hands back a file to upload.
 *
 * JPEG, on white. A photograph as PNG at this size is several hundred
 * kilobytes for no visible gain — but JPEG has no transparency, so a PNG with
 * a clear background would come back with black behind it unless something is
 * painted under it first.
 *
 * White is a choice with a cost, and the cost is worth writing down: the admin
 * defaults to a dark theme, so a picture with a transparent background becomes
 * a white disc on a near-black card rather than blending into it. The trade is
 * still the right way round — photographs are what people upload, they have no
 * transparency, and a photograph as PNG is five times the bytes — but a logo
 * with a clear background will look deliberate rather than transparent.
 *
 * The geometry is all in `crop.ts` and tested there; what is left here is the
 * three calls that draw it, and the seam between them is what this module's
 * own test drives.
 */
export const cropToFile = async (
  image: CanvasImageSource,
  rect: SourceRect,
  name = 'avatar.jpg',
): Promise<File> => {
  const canvas = document.createElement('canvas');
  canvas.width = AVATAR_SIZE;
  canvas.height = AVATAR_SIZE;

  const context = canvas.getContext('2d');
  if (!context) throw new Error('This browser cannot prepare the photo.');

  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, AVATAR_SIZE, AVATAR_SIZE);
  context.drawImage(image, rect.x, rect.y, rect.size, rect.size, 0, 0, AVATAR_SIZE, AVATAR_SIZE);

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', 0.9),
  );
  if (!blob) throw new Error('This browser could not prepare the photo.');

  return new File([blob], name, { type: 'image/jpeg' });
};
